import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '@/app/store'
import brandManager from '@/services/brandManager'
import { getAuthToken } from '@/utils/secureStorage'
import { buildEndpoint, getPlatformHeader } from '@/utils/apiUtils'

// CS Flow API
export const csFlowApi = createApi({
  reducerPath: 'csFlowApi',
  baseQuery: fetchBaseQuery({
    baseUrl: brandManager.apiUrl,
    prepareHeaders: (headers, { getState }) => {
      // Try to get token from Redux state first
      let token = (getState() as RootState).auth.token
      
      // If not in Redux state, get from secure storage
      if (!token) {
        token = getAuthToken()
      }
      
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
      
      // Add language header
      const lang = localStorage.getItem('language') || 'ar'
      headers.set('lang', lang)
      
      // Add platform header
      headers.set('platform', getPlatformHeader())
      
      headers.set('Content-Type', 'application/json')
      headers.set('Accept', 'application/json')
      
      return headers
    },
  }),
  tagTypes: ['CsFlow', 'CsCart', 'Branch', 'Address', 'CarData'],
  endpoints: (builder) => ({
    // Search customer by phone — uses /cashier or /customerService via buildEndpoint
    searchCustomerByPhone: builder.query<{
      success: boolean
      data: Array<{
        id: number
        name: string
        phone: string
      }>
      message?: string
    }, { phone: string }>({
      query: ({ phone }) => ({
        url: buildEndpoint('/cashier/users/listByPhone'),
        method: 'GET',
        params: { phone },
      }),
      providesTags: ['CsFlow'],
    }),

    // Get cart data by customer
    getCsCartData: builder.query<{
      success: boolean
      data: {
        id: string
        user_id: number
        branch_id: number
        type_id: number
        address_id?: number
        table_id?: number
        sub_total: string
        tax_value: string
        total_price: string
        discount: string
        delivery_price: string
        table_price: string
        items: Array<any>
        branch?: any
        address?: any
      }
      message?: string
    }, { customerId: number; couponCode?: string }>({
      query: ({ customerId, couponCode }) => {
        const params: Record<string, string> = {}
        if (couponCode && couponCode.trim().length > 0) {
          params.coboneCode = couponCode.trim()
        }

        return {
          url: buildEndpoint(`/cashier/carts/user/${customerId}`, customerId),
          method: 'GET',
          params,
        }
      },
      providesTags: ['CsCart'],
    }),

    // Change cart type
    changeCartType: builder.mutation<{
      success: boolean
      data: any
      message?: string
    }, {
      cartId: string
      userId: number
      typeId: number
      branchId: number
      addressId?: number
    }>({
      query: ({ cartId, userId, typeId, branchId, addressId }) => {
        const body: any = {
          userId,
          typeId,
          branchId,
        }
        
        if (typeId === 1 && addressId) {
          body.addressId = addressId
        }
        
        return {
          url: buildEndpoint(`/cashier/carts/${cartId}/change-type`),
          method: 'PUT',
          body,
        }
      },
      invalidatesTags: ['CsCart'],
    }),

    // Get employee branches
    getEmployeeBranches: builder.query<{
      success: boolean
      data: {
        branches: Array<{
          id: number
          name: string
          descriptive_address?: string
          active: boolean
          busy: boolean
          is_open: boolean
          working_from?: string
          working_to?: string
        }>
      }
      message?: string
    }, {}>({
      query: () => ({
        url: buildEndpoint('/cashier/branches/employee'),
        method: 'GET',
      }),
      providesTags: ['Branch'],
    }),

    // Get customer addresses
    getCustomerAddresses: builder.query<{
      success: boolean
      data: Array<{
        id: number
        customer_id: number
        label: string
        address: string
        descriptive_address?: string
        latitude?: number
        longitude?: number
        type?: string
      }>
      message?: string
    }, { userId: number }>({
      query: ({ userId }) => ({
        url: buildEndpoint(`/cashier/users/${userId}/addresses`),
        method: 'GET',
      }),
      providesTags: ['Address'],
    }),

    // Save new address
    saveNewAddress: builder.mutation<{
      success: boolean
      data?: {
        id: number
        customer_id: number
        label: string
        address: string
        descriptive_address?: string
        latitude?: number
        longitude?: number
        type?: string
      }
      message?: string
    }, {
      userId: number
      name: string
      latitude: number
      longitude: number
      descriptiveAddress: string
      type: string
    }>({
      query: ({ userId, name, latitude, longitude, descriptiveAddress, type }) => ({
        url: buildEndpoint(`/cashier/users/${userId}/address`),
        method: 'POST',
        body: {
          address_name: name,
          latitude: latitude.toString(),
          longitude: longitude.toString(),
          descriptive_address: descriptiveAddress,
          type,
        },
      }),
      transformResponse: (response: any, meta) => {
        // Handle 204 No Content - return success object
        // Check if response is empty or status is 204
        if (!response || meta?.response?.status === 204) {
          return { success: true }
        }
        // Handle normal response with body
        // If response doesn't have success field, add it
        if (response && typeof response === 'object' && !response.hasOwnProperty('success')) {
          return { ...response, success: true }
        }
        return response
      },
      invalidatesTags: ['Address'],
    }),

    // Get car models and colors
    getCarModelsAndColors: builder.query<{
      success: boolean
      data: {
        models: Array<any>
        colors: Array<any>
      }
      message?: string
    }, {}>({
      query: () => ({
        url: '/webApp/settings/car-models-colors',
        method: 'GET',
      }),
      providesTags: ['CarData'],
    }),
  }),
})

// Export hooks
export const {
  useSearchCustomerByPhoneQuery,
  useGetCsCartDataQuery,
  useChangeCartTypeMutation,
  useGetEmployeeBranchesQuery,
  useGetCustomerAddressesQuery,
  useSaveNewAddressMutation,
  useGetCarModelsAndColorsQuery,
} = csFlowApi



